// Reading plans over the real API (biblestandard-api F5, 08-READING-PLANS.md §3), through the typed
// client. The API's shapes are the app's (plans.ts), so the mapping is only the people on them
// (avatars through the same origin as the rest) and errors in words. The reader is the session's:
// user ids here only ever name someone to invite.
import { createApi, type ApiClient } from "../api/client";
import type { components } from "../api/schema";
import { localDate, ok, toCard } from "./api";
import { ApiError } from "./problem";
import type { DayNote, Invitation, Journey, Plan, PlanSource, PlanSummary } from "./plans";

type S = components["schemas"];

const toSummary = (p: S["PlanSummary"]): PlanSummary => ({ ...p, author: p.author ? toCard(p.author) : null });
const toPlan = (p: S["Plan"]): Plan => ({ ...toSummary(p), schedule: p.schedule });
const toJourney = (j: S["Journey"]): Journey => ({
  ...j,
  plan: { ...toSummary(j.plan), ...(j.plan.schedule ? { schedule: j.plan.schedule } : {}) },
  members: j.members.map((m) => ({ ...m, user: toCard(m.user) })),
  invited: j.invited.map(toCard),
});
const toInvitation = (i: S["PlanInvitation"]): Invitation => ({
  ...i,
  from: i.from ? toCard(i.from) : null,
  plan: toSummary(i.plan),
  members: i.members.map(toCard),
});
const toNote = (n: S["JourneyNote"]): DayNote => ({ ...n, author: toCard(n.author) });

/** Not there or not the reader's (the API answers both 404), or not an id at all (400): null. */
async function orNull<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 400)) return null;
    throw e;
  }
}

/** Plan ids and journey ids are UUIDs; anything else (an old prototype link) is simply not there. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function createApiPlans(opts: { baseUrl: string; fetch?: typeof fetch } | { api: ApiClient }): PlanSource {
  const api = "api" in opts ? opts.api : createApi(opts);
  const id = (journeyId: string) => ({ params: { path: { id: journeyId } } });

  return {
    async plans() {
      return (await ok(api.GET("/v1/plans"))).items.map(toSummary);
    },
    async plan(planId) {
      if (!UUID.test(planId)) return null;
      const p = await orNull(ok(api.GET("/v1/plans/{planId}", { params: { path: { planId } } })));
      return p ? toPlan(p) : null;
    },
    async createPlan(input) {
      return toPlan(
        await ok(
          api.POST("/v1/plans", {
            body: {
              title: input.title.trim(),
              description: input.description.trim() || null,
              books: input.books as S["PlanInput"]["books"],
              days: input.days,
              restSundays: input.restSundays,
              visibility: input.visibility,
              ...(input.alongside ? { alongside: input.alongside } : {}),
            },
          }),
        ),
      );
    },
    async journeys() {
      return (await ok(api.GET("/v1/journeys", { params: { query: { today: localDate() } } }))).items.map(toJourney);
    },
    async journey(journeyId) {
      if (!UUID.test(journeyId)) return null;
      const j = await orNull(ok(api.GET("/v1/journeys/{id}", { params: { path: { id: journeyId }, query: { today: localDate() } } })));
      return j ? toJourney(j) : null;
    },
    async start(planId, { together, invite, startOn, follow }) {
      return toJourney(await ok(api.POST("/v1/journeys", { body: { planId, together, invite, startOn, ...(follow ? { follow } : {}) } })));
    },
    async setRead(journeyId, day, read) {
      const params = { params: { path: { id: journeyId, day } } };
      return toJourney(await ok(read ? api.PUT("/v1/journeys/{id}/days/{day}", params) : api.DELETE("/v1/journeys/{id}/days/{day}", params)));
    },
    async setPaused(journeyId, paused) {
      return toJourney(await ok(api.PATCH("/v1/journeys/{id}", { ...id(journeyId), body: { paused } })));
    },
    async catchUp(journeyId, how, today) {
      return toJourney(await ok(api.PATCH("/v1/journeys/{id}", { ...id(journeyId), body: { catchUp: how, today } })));
    },
    async invite(journeyId, userIds) {
      await ok(api.POST("/v1/journeys/{id}/invitations", { ...id(journeyId), body: { userIds } }));
    },
    async withdrawInvite(journeyId, userId) {
      await ok(api.DELETE("/v1/journeys/{id}/invitations/{userId}", { params: { path: { id: journeyId, userId } } }));
    },
    async join(code) {
      if (!/^[a-z0-9]{6,20}$/.test(code)) throw new ApiError("This invite link isn’t valid. Ask for a new one.", "not-found", 404);
      try {
        return toJourney(await ok(api.POST("/v1/journeys/join/{code}", { params: { path: { code } } })));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404)
          throw new ApiError("This invite link isn’t valid any more, or someone in the plan has blocked you. Ask for a new one.", e.type, 404);
        throw e;
      }
    },
    async leave(journeyId) {
      await ok(api.DELETE("/v1/journeys/{id}/membership", id(journeyId)));
    },
    async invitations() {
      return (await ok(api.GET("/v1/invitations"))).items.map(toInvitation);
    },
    async respond(journeyId, accept) {
      const r = await ok(api.POST("/v1/invitations/{journeyId}", { params: { path: { journeyId } }, body: { accept } }));
      // 204 (declined) has no body.
      return r ? toJourney(r) : null;
    },
    async notes(journeyId, day) {
      const r = await ok(api.GET("/v1/journeys/{id}/days/{day}/notes", { params: { path: { id: journeyId, day } } }));
      return { prompt: r.prompt, items: r.items.map(toNote) };
    },
    async addNote(journeyId, day, body) {
      return toNote(await ok(api.POST("/v1/journeys/{id}/days/{day}/notes", { params: { path: { id: journeyId, day } }, body: { body } })));
    },
    async deleteNote(journeyId, noteId) {
      await ok(api.DELETE("/v1/journeys/{id}/notes/{noteId}", { params: { path: { id: journeyId, noteId } } }));
    },
  };
}
